import { TFileResponse } from 'modules/types';
import { TMentor, TMentee, TUser } from 'modules/user';
import { EPayMethod } from 'enums/base.enum';
import { EMentoringClassStatus, EFeedbackScore } from 'enums/mentoring.enum';

/**
 * 멘토링 수업
 */
export type TMentoringClassCreate = {
  mentor_id: number;
  desired_topic_ids: number[];
  file_ids: number[];
  requirement: string;
  start_at: string;
  end_at: string;
};

export type TMentoringClassCreateForm = {
  desired_topic_ids: number[];
  file_ids: number[];
  requirement: string;
  selected_schedules: Array<{
    start_at: string;
    end_at: string;
    mentor_id: number;
  }>;
};

export type TMentoringClass = {
  id: number;
  mentor: TMentor;
  mentee: TMentee;
  desired_topics: Array<TMentoringClassTopic>;
  files: Array<TFileResponse>;
  requirement: string;
  status: EMentoringClassStatus;
  start_at: string;
  end_at: string;
  remaining_hours: number;
  feedback: number;
  is_feedback_completed: boolean;
  review: number;
  is_mentee_attended: boolean;
  is_mentor_attended: boolean;
  memo: string;
  created_at: string;
  updated_at: string;
  sendbird: TSendbirdInfo;
};

export type TSendbirdInfo = {
  application_id: string;
  my_sendbird_id: string;
  partner_sendbird_id: string;
};

export type TUpdateMentoringClassSchedule = {
  start_at: string;
  end_at: string;
};

export type TUpdateMentoringClass = {
  file_ids: Array<number>;
  desired_topic_ids: Array<number>;
  requirement: string;
};

export type TMentoringClassTopic = {
  id: number;
  name: string;
};

export type TMentoringCancel = {
  memo: string;
};

export type TMentoringReject = {
  memo: string;
};

/**
 * Available schedule
 */
export type TAvailableSchedule = {
  time_slice: number;
  available_dates?: any;
};

export type TWeekSchedule = {
  date_from: string;
  date_to?: string;
  with_class_id?: string | number;
};

export type TSelectedTimeSlice = {
  time_slice: number;
  date_at: string;
};

export type TMentoringFeedback = {
  overall: string;
  effectiveness: string;
  problemsolving: string;
  communication: string;
  overall_score: EFeedbackScore | number | null;
  effectiveness_score: EFeedbackScore | number | null;
  problemsolving_score: EFeedbackScore | number | null;
  communication_score: EFeedbackScore | number | null;

  mentoring_class_id: number;
  is_completed: boolean;
  case_tutorial: boolean;

  analytical_thinking: string;
  motivation_to_develop: string;
  technical_skill: string;
  finance_communication: string;
  analytical_thinking_score: EFeedbackScore | number | null;
  motivation_to_develop_score: EFeedbackScore | number | null;
  technical_skill_score: EFeedbackScore | number | null;
  finance_communication_score: EFeedbackScore | number | null;
};

/**
 * Review
 */
export type TMentoringReview = {
  score: number;
  comment: string;
  mentoring_class_id?: number;
  mentoring_class?: TMentoringClass;
  created_at: string;
};

export type TMentoringReview2 = {
  score: number;
  comment: string;
  comment_extra: string;
  score_contents: number;
  score_communication: number;
  score_professionalism: number;
  tags: Array<number>;
  mentoring_class_id?: number;
  mentoring_class?: TMentoringClass;
  created_at: string;
};

export type TMentoringReviewsForLanding = {
  reviews: Array<TMentoringReview>;
  total: number;
};

export type TMentoringPassCase = {
  title: string;
  comment: string;
};

export type TMentoringPassCasesForLanding = {
  reviews: Array<TMentoringPassCase>;
  total: number;
};

/**
 * 멘토링 크레딧 결제
 */
export type TCreatePaymentLog = {
  plan_index: number;
  pay_method: EPayMethod;
  credit: number;
};

export type TVerifyPaymentLog = {
  result: string;
  error_message?: string;
  imp_uid: string;
  merchant_uid: string;
};

export type TVerifymobilePaymentLog = {
  imp_success: string;
  imp_uid: string;
  merchant_uid: string;
};

export type TCancelPaymentLog = {
  merchant_uid: string;
  cancel_request_amount: number;
  reason: string;
  refund_holder?: string;
  refund_bank?: string;
  refund_account?: string;
};

export type TPaymentLog = {
  user: TUser;
  amount: number;
  status: string;
  pay_method: EPayMethod;
};

export type TFeedback = {
  month?: string;
  name: string;
  '내 점수': number;
  '전체 평균'?: number;
  '합격자 평균'?: number;
};
export type TCompanyFeedback = {
  company_name: string;
  rating_count: number;
  color: string;
  data: Array<TCompanyFeedbackData>;
};
export type TCompanyFeedbackData = {
  feedback: string;
  score: number;
  fullMark: number;
};
export type TMentorFeedbackData = {
  data: {
    company_name?: string;
    mentor?: {
      fullname?: string;
      id: number;
      is_current: boolean;
      profile_iamge?: string;
      mentor_type?: string;
    };
    mentor_scores: {
      communication_score: number;
      impact_score: number;
      overall_score: number;
      problemsolving_score: number;
    };
    my_scores: {
      communication_score: number;
      feedback_count: number;
      impact_score: number;
      overall_score: number;
      problemsolving_score: number;
    };
  };
};

//네이밍 변경 후
export enum EMentoringTicketName {
  RegularPackage = 'Career Step 정규반 수강권',
  TutorialPackage = '튜토리얼 패키지 수강권',
  MockInterviewPackage = '실전 Mock 인터뷰 패키지',
  MockInterviewSenior = '시니어 멘토 실전 Mock 인터뷰',
  MockInterviewJunior = '주니어 멘토 실전 Mock 인터뷰',
}

//결제 페이지 URLQuery
export enum EMentoringPurchaseURLQuery {
  RegularPackage = 'regular',
  TutorialPackage = 'tutorial',
  MockInterviewPackage = 'mock_package',
  MockInterviewSenior = 'mock_senior',
  MockInterviewJunior = 'mock_junior',
}
export enum EFinanceMentoringPurchaseURLQuery {
  MockInterview60Minutes = '60minutes',
}

//수강권 네이밍 - 컨설팅 수강권 구매 결제페이지 컴포넌트에서 쓰임
export const CMentoringTicketName: { [key: string]: string } = {
  [EMentoringPurchaseURLQuery.RegularPackage]: 'Career Step 정규반 수강권',
  [EMentoringPurchaseURLQuery.TutorialPackage]: '튜토리얼 패키지 수강권',
  [EMentoringPurchaseURLQuery.MockInterviewPackage]: '실전 Mock 인터뷰 패키지',
  [EMentoringPurchaseURLQuery.MockInterviewSenior]: '시니어 멘토 실전 Mock 인터뷰',
  [EMentoringPurchaseURLQuery.MockInterviewJunior]: '주니어 멘토 실전 Mock 인터뷰',
  [EFinanceMentoringPurchaseURLQuery.MockInterview60Minutes]: 'Finance Mock 인터뷰 이용권(60분)',
};

export type TMentoringTicketInfo = {
  id: number;
  mentoring_ticket_name: string;
  mentoring_ticket_price: string;
};

//수강권 현황 페이지 각각 멘토링 수강권 (O) 티켓별 balance 설정을 위해
//여기서의 balance - 주니어/시니어 balance에는 1회권 & 목패키지 주니어/시니어 티켓 내용이 한번에

//멘토링 수강권 - consulting - balance
export enum EMentoringTicketBalance {
  RegularTicketBalance = 'careerstep_balance',
  JuniorTicketBalance = 'junior_balance',
  SeniorTicketBalance = 'senior_balance',
  SpecialTicketBalance = 'special_balance',
  SpecialSeniorTicketBalance = 'senior_special_balance',
}
//멘토링 수강권 - Finance - balance
export enum EFinanceMentoringTicketBalance {
  // '30Minutes' = 'careerstep_balance', //finance balance 데이터 받을 때의key 값과 일치해야한다
  '60Minutes' = 'mock_interview_60mins_balance',
}
//멘토링 수강권 - consulting &  Finance - balance
export const CMentoringTicketBalanceName: { [key: string]: string } = {
  [EMentoringTicketBalance.RegularTicketBalance]: '커리어스텝 정규반 수강권',
  [EMentoringTicketBalance.JuniorTicketBalance]: '주니어 멘토 Mock Interview',
  [EMentoringTicketBalance.SeniorTicketBalance]: '시니어 멘토 Mock Interview',
  [EMentoringTicketBalance.SpecialTicketBalance]: '주니어 멘토 Tutorial',
  [EMentoringTicketBalance.SpecialSeniorTicketBalance]: '시니어 멘토 Tutorial',
  [EFinanceMentoringTicketBalance['60Minutes']]: 'Mock 인터뷰 이용권(60분)', //마이페이지 멘토링 수강권 이름
};

// 그 외 수강권 - consulting - balance(이용권 자체)
//수강권 현황 페이지 멘토링 수강권 (X) 그 외 수강권 (O)
export enum EMembershipBalance {
  caseQuiz = 'case_quiz',
  excelVideo = 'excel_video',
  interviewMasterClassBasic = 'interview_master_class_basic',
}
//그 외 수강권 - Finance - balance(이용권 자체)
export enum EFinanceMembershipBalance {
  FinanceFrameworks = 'finance_frameworks',
}
// 그 외 수강권 - consulting & Finance - balance(이용권 자체)
export const CMembershipBalanceName: { [key: string]: string } = {
  [EMembershipBalance.caseQuiz]: '케이스 문제 3개월 무제한 이용권',
  [EMembershipBalance.excelVideo]: '엑셀 강의 3개월 무제한 이용권',
  [EMembershipBalance.interviewMasterClassBasic]:
    '전략컨설팅 인터뷰 MasterClass [기초편]\n3개월 무제한 이용권',
  [EFinanceMembershipBalance.FinanceFrameworks]: 'Finance Frameworks 무제한 이용권',
};

//사용및 구매 내역 페이지에서 들어오는 값 에따라 deposit, withdraw 횟수 설정해주기위해
export enum EMentoringTicketBackendNaming {
  RegularPackage = 'Career Step 정규반 수강권',
  TutorialPackage = '튜토리얼 패키지 수강권',
  MockInterviewPackage = '실전 Mock 인터뷰 패키지',
  MockInterviewSenior = '시니어 멘토 실전 Mock 인터뷰',
  MockInterviewJunior = '주니어 멘토 실전 Mock 인터뷰',
}
export enum EFinanceMentoringTicketBackendNaming {
  '60Minutes' = 'Finance Mock 인터뷰 이용권(60분)',
}

export const CMentoringTicketOriginNumber: { [key: string]: number } = {
  [EMentoringTicketBackendNaming.RegularPackage]: 5,
  [EMentoringTicketBackendNaming.TutorialPackage]: 5,
  [EMentoringTicketBackendNaming.MockInterviewPackage]: 5,
  [EMentoringTicketBackendNaming.MockInterviewSenior]: 1,
  [EMentoringTicketBackendNaming.MockInterviewJunior]: 1,
};

//목인터뷰 패키지는 따로 키값 하드코딩하여 주니어 환불가격, 시니어 환불가격 받아준다
export const CMentoringTicketRefundKeyname: { [key: string]: string } = {
  [EMentoringTicketBackendNaming.RegularPackage]: 'careerstep_ticket',
  [EMentoringTicketBackendNaming.TutorialPackage]: 'special_ticket',
  // [EMentoringTicketBackendNaming.MockInterviewPackage]: 5,
  [EMentoringTicketBackendNaming.MockInterviewSenior]: 'single_senior_ticket',
  [EMentoringTicketBackendNaming.MockInterviewJunior]: 'single_junior_ticket',
  [EFinanceMentoringTicketBackendNaming['60Minutes']]: 'mock_interview_60mins_ticket',
};

//목인터뷰 패키지는 따로 키값 하드코딩하여 senior_deposit+ junior_deposit
export const CMentoringTicketDepositKeyname: { [key: string]: string } = {
  [EMentoringTicketBackendNaming.RegularPackage]: 'careerstep_deposit',
  [EMentoringTicketBackendNaming.TutorialPackage]: 'special_deposit',
  [EMentoringTicketBackendNaming.MockInterviewSenior]: 'single_senior_deposit',
  [EMentoringTicketBackendNaming.MockInterviewJunior]: 'single_junior_deposit',
};
export const CFinanceMentoringTicketDepositKeyname: { [key: string]: string } = {
  [EFinanceMentoringTicketBackendNaming['60Minutes']]: 'mock_interview_60mins_deposit',
};

//목인터뷰 패키지는 따로 키값 하드코딩하여 senior_withdraw + junior_withdraw
export const CMentoringTicketWithdrawKeyname: { [key: string]: string } = {
  [EMentoringTicketBackendNaming.RegularPackage]: 'careerstep_withdraw',
  [EMentoringTicketBackendNaming.TutorialPackage]: 'special_withdraw',
  [EMentoringTicketBackendNaming.MockInterviewSenior]: 'single_senior_withdraw',
  [EMentoringTicketBackendNaming.MockInterviewJunior]: 'single_junior_withdraw',
};
export const CFinanceMentoringTicketWithdrawKeyname: { [key: string]: string } = {
  [EFinanceMentoringTicketBackendNaming['60Minutes']]: 'mock_interview_60mins_withdraw',
};

export const CSeniorTicketOriginNum = 3;
export const CJuniorTicketOriginNum = 2;
export const CMentoringPackagesAmount = 1500000;
// 마이페이지 - consulting - 티켓 프레임
export const MENTORING_COURSES_LIST = [
  /*{
    course_title: '튜토리얼 패키지 수강권',
    course_name: [
      EMentoringTicketBalance.SpecialTicketBalance,
      EMentoringTicketBalance.SpecialSeniorTicketBalance,
    ],
  },*/
  {
    course_title: '실전 Mock 인터뷰 패키지',
    course_name: [EMentoringTicketBalance.JuniorTicketBalance, EMentoringTicketBalance.SeniorTicketBalance],
  },
  /*{
    course_title: 'Career Step 정규반 수강권',
    course_name: [EMentoringTicketBalance.RegularTicketBalance],
  },*/
];
//멘티페이지 or 멘토링신청 페이지에서 내 크레딧 현황 보여줄 떄 - consulting
export const CURRENT_TICKET_BALANCE = [
  {
    ticket_name: '정규반 수강권',
    key_name: EMentoringTicketBalance.RegularTicketBalance,
  },
  {
    ticket_name: '튜토리얼 패키지(주니어)',
    key_name: EMentoringTicketBalance.SpecialTicketBalance,
  },
  {
    ticket_name: '튜토리얼 패키지(시니어)',
    key_name: EMentoringTicketBalance.SpecialSeniorTicketBalance,
  },
  {
    ticket_name: '실전 Mock 인터뷰 (시니어)',
    key_name: EMentoringTicketBalance.SeniorTicketBalance,
  },
  {
    ticket_name: '실전 Mock 인터뷰 (주니어)',
    key_name: EMentoringTicketBalance.JuniorTicketBalance,
  },
];

//멘티페이지 or 멘토링신청 페이지에서 내 크레딧 현황 보여줄 떄 - finance
export const CURRENT_FINANCE_TICKET_BALANCE = [
  {
    ticket_name: 'Mock 인터뷰 이용권(60분)',
    key_name: EFinanceMentoringTicketBalance['60Minutes'], //finance balance 데이터 받을 때의key 값과 일치해야한다
  },
];

//마이페이지 - finance - 티켓 프레임
export const FINANCE_MENTORING_COURSES_LIST = [
  {
    course_title: 'Mock 인터뷰',
    course_name: [EFinanceMentoringTicketBalance['60Minutes']],
  },
];
